import { Fragment, Suspense, useEffect, useMemo } from "react";
import { useLazyLoadQuery } from "react-relay";

import MainErrorBoundary from "./ErrorBoundaries/MainErrorBoundary";
import dispatchEvent from "./Utils/dispatchEvent";

const AppQueryRendererInner = ({
  query,
  variables = {},
  fetchPolicy = "store-or-network",
  ...props
}) => {
  // We need the `component` variable to be pascal cased for React to treat it as a custom
  // component in the below JSX call
  const { additionalProps, component: Component, children } = props;

  const data = useLazyLoadQuery(query, variables, { fetchPolicy });

  // When the query is loaded from the server, refresh its container width and trigger a
  // newcontentloaded event to allow its parent to resize if needed.
  useEffect(() => {
    if (!data) return;

    dispatchEvent(window, "newContentLoaded");
  }, [data]);

  // Merge Relay props with provided additionalProps and the query's result data
  const componentProps = useMemo(() => {
    if (!data) return null;

    return {
      variables,
      fetchPolicy,
      ...additionalProps,
      ...props,
      ...data
    };
  }, [variables, fetchPolicy, additionalProps, props, data]);

  const suspenseContainer = useMemo(() => {
    if (!componentProps) return null;

    if (Component) return <Component {...componentProps} />;
    if (children) return children(componentProps);

    return null;
  }, [Component, children, componentProps]);

  return suspenseContainer;
};

// This is the wrapper that allows us to show a loading spinner while the query is being
// fetched when the component is provided with a `loader` prop. When no loader is provided
// the query will be rendered without a fallback and react-router will handle the
// transition state by keeping the previous view displayed until the new content is
// rendered.
const AppQueryRenderer = ({
  loader,
  handleErrors = true,
  ErrorBoundary = MainErrorBoundary,
  ...props
}) => {
  // Use a flat component tree structure and check whether to use error boundary and
  // suspense before, as otherwise it would create many combinations and be less readable.
  const QueryErrorBoundary = handleErrors ? ErrorBoundary : Fragment;
  const SuspenseWrapper = loader ? Suspense : Fragment;
  const suspenseProps = loader ? { fallback: loader } : {};

  return (
    <QueryErrorBoundary>
      <SuspenseWrapper {...suspenseProps}>
        <AppQueryRendererInner {...props} />
      </SuspenseWrapper>
    </QueryErrorBoundary>
  );
};

export default AppQueryRenderer;
